/** Mirrors MEDIA_MAX_BYTES in backend/src/routes/connector.ts. */
export const MEDIA_UPLOAD_MAX_BYTES = 16 * 1024 * 1024;

/** 7-Day day uploaders keep their own, tighter caps. */
export const ENGAGEMENT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const ENGAGEMENT_PDF_MAX_BYTES = 10 * 1024 * 1024;
export const ENGAGEMENT_VIDEO_MAX_BYTES = 16 * 1024 * 1024;

export const VOICE_RECORDING_MAX_BYTES = 10 * 1024 * 1024;

export function formatUploadSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

/** Shown when a picked file is over the cap; the file is not uploaded. */
export function fileTooLargeMessage(sizeBytes: number, limitBytes: number, label = 'This file'): string {
  return `${label} is ${formatUploadSize(sizeBytes)}. Uploads are capped at ${formatUploadSize(limitBytes)} — choose a smaller file.`;
}

export function recordingTooLargeMessage(sizeBytes: number): string {
  return `This recording is ${formatUploadSize(sizeBytes)}. Voice notes are capped at ${formatUploadSize(VOICE_RECORDING_MAX_BYTES)} — record a shorter one.`;
}

export type UploadMediaKind = 'image' | 'video' | 'document' | 'voice';

/** Same allowlist as MEDIA_ALLOWED_MIME on the backend, per library media type. */
const ALLOWED_MIME_BY_KIND: Record<UploadMediaKind, string[]> = {
  image: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
  video: ['video/mp4', 'video/quicktime'],
  document: ['application/pdf'],
  voice: ['audio/webm', 'audio/ogg', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav'],
};

const KIND_HINT: Record<UploadMediaKind, string> = {
  image: 'a PNG, JPG or WEBP image',
  video: 'an MP4 or MOV video',
  document: 'a PDF',
  voice: 'an MP3, M4A, WAV, OGG or WEBM audio file',
};

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
};

/**
 * The file's MIME type, falling back to its extension when the browser leaves
 * it blank or generic (some devices report PDFs as application/octet-stream).
 */
export function effectiveMimeType(file: File): string {
  const reported = (file.type || '').toLowerCase().split(';')[0].trim();
  if (reported && reported !== 'application/octet-stream') return reported;
  const ext = /\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1]?.toLowerCase() || '';
  return MIME_BY_EXTENSION[ext] || reported;
}

/** The bytes to encode, typed so the data URL carries the effective MIME type. */
export function asTypedUploadBlob(file: File): Blob {
  const type = effectiveMimeType(file);
  return type && type !== file.type ? new Blob([file], { type }) : file;
}

/** Null when the file can be uploaded as `kind`; otherwise the reason it cannot. */
export function libraryUploadIssue(file: File, kind: UploadMediaKind): string | null {
  const type = effectiveMimeType(file);
  if (!ALLOWED_MIME_BY_KIND[kind].includes(type)) {
    return `"${file.name}" is not ${KIND_HINT[kind]}. Choose ${KIND_HINT[kind]}.`;
  }
  if (file.size > MEDIA_UPLOAD_MAX_BYTES) {
    return fileTooLargeMessage(file.size, MEDIA_UPLOAD_MAX_BYTES, `"${file.name}"`);
  }
  return null;
}

/** Caption under an upload control, e.g. "PDF · max 16 MB". */
export function uploadCapHint(kind: UploadMediaKind, limitBytes = MEDIA_UPLOAD_MAX_BYTES): string {
  const label = kind === 'image' ? 'PNG, JPG or WEBP'
    : kind === 'video' ? 'MP4 or MOV'
      : kind === 'document' ? 'PDF'
        : 'Audio';
  return `${label} · max ${formatUploadSize(limitBytes)}`;
}
